/**
 * Distance unit handling for the mission planner (issue #294).
 *
 * All internal distances are kilometres (the unit `calculateDistance` returns).
 * These helpers convert/format to the operator's chosen display unit.
 */

export type DistanceUnit = 'm' | 'km' | 'mi' | 'nmi';

/** Options for the unit selector, in display order. */
export const DISTANCE_UNITS: ReadonlyArray<{
  value: DistanceUnit;
  label: string;
}> = [
  { value: 'm', label: 'Meters' },
  { value: 'km', label: 'Kilometers' },
  { value: 'mi', label: 'Miles' },
  { value: 'nmi', label: 'Nautical miles' },
];

/** Multipliers from kilometres to each unit. */
const KM_TO: Record<DistanceUnit, number> = {
  m: 1000,
  km: 1,
  mi: 0.621371, // 1 km = 0.621371 mi
  nmi: 0.539957, // 1 km = 0.539957 nautical miles
};

/** Short suffix shown next to a value. */
const SUFFIX: Record<DistanceUnit, string> = {
  m: 'm',
  km: 'km',
  mi: 'mi',
  nmi: 'NM',
};

/** Convert a kilometre value to the given unit. */
export const convertKm = (km: number, unit: DistanceUnit): number =>
  km * KM_TO[unit];

/**
 * Sub-unit a display unit steps down to for short distances (issue #433): an
 * 18 m leg reads `18 m`, not `0.02 km`, which two decimals of a kilometre
 * cannot distinguish from any other short leg.
 *
 * Only the large units step down, and only within their own measurement
 * system. An operator who explicitly picked metres or nautical miles keeps
 * exactly that (#294) — the selector is their choice, this only picks a
 * sensible sub-unit inside it.
 */
const SUB_UNIT: Partial<
  Record<DistanceUnit, { belowKm: number; perKm: number; suffix: string }>
> = {
  km: { belowKm: 1, perKm: 1000, suffix: 'm' },
  mi: { belowKm: 0.160934, perKm: 3280.84, suffix: 'ft' }, // below 0.1 mi
};

/**
 * Format a kilometre value in the given unit, e.g. `formatDistanceUnit(1.5,
 * 'm')` → `"1500 m"`. Metres show no decimals; larger units show two, and
 * step down to their sub-unit below the {@link SUB_UNIT} threshold.
 */
export const formatDistanceUnit = (km: number, unit: DistanceUnit): string => {
  const sub = SUB_UNIT[unit];
  if (sub && Math.abs(km) < sub.belowKm) {
    return `${(km * sub.perKm).toFixed(0)} ${sub.suffix}`;
  }
  const value = convertKm(km, unit);
  const decimals = unit === 'm' ? 0 : 2;
  return `${value.toFixed(decimals)} ${SUFFIX[unit]}`;
};
